import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import {
    SUPPORTED_UNITS_SCHEMA,
    CUSTOM_TRANSPORT_PRODUCT_TYPES_SCHEMA,
    CUSTOM_WASTE_DISPOSAL_PRODUCT_TYPES_SCHEMA,
    CUSTOM_WASTE_RECYCLING_PRODUCT_TYPES_SCHEMA,
    FREIGHT_TYPE,
    DISPOSAL_PURPOSE,
    PRODUCT_TYPES,
    RequiredDisposalCustomQuotePayloadSchema,
    FREIGHT_TYPE_SCHEMA,
    DISPOSAL_PURPOSE_SCHEMA,
} from '@schuettflix/interfaces';
import { CurrencyCodeSchema } from '@schuettflix/utils';
import { isValid as isValidDate, parseISO } from 'date-fns';
import { EXCLUDED_PARTNER_ORGANIZATION_IDS } from '@/shared/constants/excludedPartnerOrganizations.ts';

const CUSTOM_WASTE_PRODUCT_TYPES_SCHEMA = z.enum([
    ...CUSTOM_WASTE_DISPOSAL_PRODUCT_TYPES_SCHEMA.options,
    ...CUSTOM_WASTE_RECYCLING_PRODUCT_TYPES_SCHEMA.options,
]);

export function useCustomRequestProductSchema(product: { type: PRODUCT_TYPES }) {
    const { t } = useTranslation();

    const baseCustomRequestProductSchema = z.object({
        amount: z
            .number({ required_error: t('product.addPosition.errorMessage.pleaseEnterValue') })
            .min(0, t('product.addPosition.errorMessage.pleaseEnterValue'))
            .positive(t('product.addPosition.errorMessage.pleaseEnterValue')),
        purchasePrice: z.string({ required_error: t('product.addPosition.errorMessage.pleaseEnterValue') }).min(1),
        salesPrice: z.string({ required_error: t('product.addPosition.errorMessage.pleaseEnterValue') }).min(1),
        unit: SUPPORTED_UNITS_SCHEMA.refine(unit => unit, t('product.addPosition.errorMessage.pleaseEnterValue')),
        partnerOrganizationId: z
            .number({ required_error: t('product.addPosition.errorMessage.pleaseEnterValue') })
            .int()
            .positive()
            .refine(id => !EXCLUDED_PARTNER_ORGANIZATION_IDS.includes(id), {
                message: t('forms.inputs.partnerSelectionInput.errorMessage.excluded'),
            }),
        serviceDate: z.string().refine(date => isValidDate(parseISO(date)), {
            message: t('product.addPosition.errorMessage.pleaseEnterValue'),
        }),
        purchaseTaxClassId: z.string({ message: t('product.addPosition.errorMessage.pleaseEnterValue') }),
        salesTaxClassId: z.string({ message: t('product.addPosition.errorMessage.pleaseEnterValue') }),
        productName: z.string({ required_error: t('product.addPosition.errorMessage.pleaseEnterValue') }).min(1),
        currencyCode: CurrencyCodeSchema,
    });

    const RequiredCustomTransportQuotePayloadSchema = z
        .object({
            freightType: z.string({
                required_error: t('product.addPosition.errorMessage.pleaseEnterValue'),
            }),
        })
        .and(
            z.discriminatedUnion('freightType', [
                z.object({
                    freightType: FREIGHT_TYPE_SCHEMA.exclude(['NON_HAZARDOUS_WASTE', 'HAZARDOUS_WASTE']),
                }),
                z.object({
                    freightType: FREIGHT_TYPE_SCHEMA.extract(['NON_HAZARDOUS_WASTE', 'HAZARDOUS_WASTE']),
                    disposalPurpose: z.enum(DISPOSAL_PURPOSE_SCHEMA.options, {
                        required_error: t('product.addPosition.errorMessage.pleaseEnterValue'),
                    }),
                }),
            ])
        );

    if (CUSTOM_TRANSPORT_PRODUCT_TYPES_SCHEMA.safeParse(product.type).success) {
        return baseCustomRequestProductSchema.and(RequiredCustomTransportQuotePayloadSchema);
    }

    if (CUSTOM_WASTE_PRODUCT_TYPES_SCHEMA.safeParse(product.type).success) {
        return baseCustomRequestProductSchema.merge(RequiredDisposalCustomQuotePayloadSchema);
    }

    return baseCustomRequestProductSchema;
}

export type CustomRequestProductSchema = z.infer<ReturnType<typeof useCustomRequestProductSchema>> & {
    freightType: FREIGHT_TYPE;
    disposalPurpose: DISPOSAL_PURPOSE;
};

type RequiredProperties<T> = {
    [K in keyof T]: T[K] | undefined;
};

export const customRequestProductSchemaDefaults: RequiredProperties<CustomRequestProductSchema> = {
    amount: undefined,
    unit: 'TON',
    partnerOrganizationId: undefined,
    purchasePrice: undefined,
    purchaseTaxClassId: 'S-DE',
    salesPrice: undefined,
    salesTaxClassId: 'S-DE',
    serviceDate: undefined,
    productName: undefined,
    currencyCode: 'EUR',
    freightType: undefined,
    disposalPurpose: undefined,
};
